import { useEffect, useRef, useState } from 'react'
import { MapPin, Clock, TrendingUp } from 'lucide-react'
import { useDestinationSuggestions } from '@/lib/queries/properties'
import { usePlaceSuggestions, placeLevelLabel, type PlaceSuggestion } from '@/lib/queries/places'
import { getRecentSearches } from '@/lib/recentSearches'
import { cn } from '@/lib/cn'

interface DestinationInputProps {
  value:            string
  onChange:         (v: string) => void
  /** Fired when a suggestion is picked (click / Enter). Carries the place's
   *  province so the caller can disambiguate + auto-search. */
  onSelect?:        (sel: { value: string; province?: string }) => void
  placeholder?:     string
  inputClassName?:  string
  /** Extra classes on the relative wrapper (e.g. width). */
  className?:       string
  /** Where the dropdown anchors horizontally. */
  align?:           'left' | 'right'
}

interface Option {
  key:       string
  title:     string
  sub?:      string
  badge?:    string   // type badge (City, Province, Area …), right-aligned
  value:     string   // what gets written into the field on select
  province?: string   // for disambiguating same-named places
  icon:      'recent' | 'pin'
}

/** Strip the leading place name from its path_label to get just the ancestors,
 *  e.g. "Mactan, Lapu-Lapu City, Cebu" → "Lapu-Lapu City, Cebu". */
function ancestorOf(p: PlaceSuggestion): string {
  return p.path_label.startsWith(p.name)
    ? p.path_label.slice(p.name.length).replace(/^,\s*/, '')
    : p.path_label
}

/**
 * Booking.com-style destination field: a text input with a dropdown showing
 * recent searches + trending destinations when empty, and live typeahead
 * suggestions while typing. Selecting an option fills the field (does not
 * auto-search).
 *
 * Two suggestion sources:
 *   • empty  → inventory "trending" (useDestinationSuggestions) — places we
 *     actually have listings, ranked by property count.
 *   • typing → the PSGC gazetteer (usePlaceSuggestions) — every real PH
 *     barangay / city / municipality / province is pickable, with a
 *     "Barangay · Lapu-Lapu City, Cebu" sub-label.
 */
export function DestinationInput({
  value, onChange, onSelect, placeholder, inputClassName, className, align = 'left',
}: DestinationInputProps) {
  const wrapRef = useRef<HTMLDivElement>(null)
  const [open, setOpen]           = useState(false)
  const [highlight, setHighlight] = useState(-1)
  const [recent, setRecent]       = useState<string[]>([])

  const trimmed  = value.trim()
  const isTyping = trimmed.length > 0

  // Debounce the term feeding the query so fast typing doesn't spam the RPC.
  const [debounced, setDebounced] = useState(trimmed)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(trimmed), 200)
    return () => clearTimeout(t)
  }, [trimmed])

  // Empty → inventory trending; typing → gazetteer places. Each is gated so
  // only the relevant source fires.
  const { data: trending = [], isFetching: trendingLoading } =
    useDestinationSuggestions('', open && !isTyping)
  const { data: places = [], isFetching: placesLoading } =
    usePlaceSuggestions(debounced, open)

  const isFetching = isTyping ? placesLoading : trendingLoading

  // Refresh recent list each time the dropdown opens.
  useEffect(() => {
    if (open) setRecent(getRecentSearches())
  }, [open])

  // Close on outside click.
  useEffect(() => {
    if (!open) return
    function onDocMouseDown(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDocMouseDown)
    return () => document.removeEventListener('mousedown', onDocMouseDown)
  }, [open])

  // ── Build the flat option list (drives both render + keyboard nav) ──────────
  const recentRows: Option[] = !isTyping
    ? recent.slice(0, 3).map((r) => ({ key: `r-${r}`, title: r, value: r, icon: 'recent' }))
    : []
  const trendingRows: Option[] = !isTyping
    ? trending.map((s) => ({
        key: `t-${s.city}-${s.province}`, title: s.city, sub: s.province,
        value: s.city, province: s.province, icon: 'pin',
      }))
    : []
  const placeRows: Option[] = isTyping
    ? places.map((p) => ({
        key:      `p-${p.code}`,
        title:    p.name,
        sub:      ancestorOf(p),                          // "Lapu-Lapu City, Cebu"
        badge:    placeLevelLabel(p.level),               // City / Province / Area …
        value:    p.name,
        province: p.province_name ?? p.region_name ?? undefined,
        icon:     'pin',
      }))
    : []

  const options: Option[] = [...recentRows, ...trendingRows, ...placeRows]

  function select(opt: Option) {
    onChange(opt.value)
    setOpen(false)
    setHighlight(-1)
    onSelect?.({ value: opt.value, province: opt.province })
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      if (!open) { setOpen(true); return }
      e.preventDefault()
      setHighlight((h) => Math.min(h + 1, options.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlight((h) => Math.max(h - 1, 0))
    } else if (e.key === 'Enter') {
      if (open && highlight >= 0 && highlight < options.length) {
        e.preventDefault()            // pick the highlighted option, don't submit yet
        select(options[highlight])
      } else {
        setOpen(false)                // nothing highlighted → let the form submit
      }
    } else if (e.key === 'Escape') {
      setOpen(false)
      setHighlight(-1)
    }
  }

  const showDropdown = open && (options.length > 0 || isFetching)

  // Flat-index offsets so each section's rows line up with keyboard highlight.
  const trendingOffset = recentRows.length
  const placeOffset    = recentRows.length + trendingRows.length

  return (
    <div ref={wrapRef} className={cn('relative', className)}>
      <input
        type="text"
        value={value}
        onChange={(e) => { onChange(e.target.value); setHighlight(-1); if (!open) setOpen(true) }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        autoComplete="off"
        role="combobox"
        aria-expanded={showDropdown}
        aria-autocomplete="list"
        className={inputClassName}
      />

      {showDropdown && (
        <div
          className={cn(
            'absolute top-full z-50 mt-2 w-[20rem] max-w-[calc(100vw-2rem)]',
            'overflow-hidden rounded-xl border border-gray-200 bg-white py-2 text-left shadow-2xl',
            align === 'right' ? 'right-0' : 'left-0',
          )}
          // Keep input focus when clicking an item.
          onMouseDown={(e) => e.preventDefault()}
        >
          {/* Recent searches (empty-query mode only) */}
          {recentRows.length > 0 && (
            <>
              <p className="px-4 pb-1 pt-1 text-xs font-semibold text-gray-500">Recent searches</p>
              {recentRows.map((opt, i) => (
                <Row
                  key={opt.key}
                  icon="recent"
                  title={opt.title}
                  active={highlight === i}
                  onMouseEnter={() => setHighlight(i)}
                  onClick={() => select(opt)}
                />
              ))}
            </>
          )}

          {/* Trending destinations (inventory-derived, empty-query mode) */}
          {trendingRows.length > 0 && (
            <>
              <p className="flex items-center gap-1.5 px-4 pb-1 pt-2 text-xs font-semibold text-gray-500">
                <TrendingUp className="h-3 w-3" aria-hidden />
                Trending destinations
              </p>
              {trendingRows.map((opt, j) => {
                const idx = trendingOffset + j
                return (
                  <Row
                    key={opt.key}
                    icon="pin"
                    title={opt.title}
                    sub={opt.sub}
                    active={highlight === idx}
                    onMouseEnter={() => setHighlight(idx)}
                    onClick={() => select(opt)}
                  />
                )
              })}
            </>
          )}

          {/* Gazetteer typeahead (typing mode) */}
          {placeRows.length > 0 && (
            <>
              <p className="px-4 pb-1 pt-2 text-xs font-semibold text-gray-500">Destinations</p>
              {placeRows.map((opt, j) => {
                const idx = placeOffset + j
                return (
                  <Row
                    key={opt.key}
                    icon="pin"
                    title={opt.title}
                    sub={opt.sub}
                    badge={opt.badge}
                    active={highlight === idx}
                    onMouseEnter={() => setHighlight(idx)}
                    onClick={() => select(opt)}
                  />
                )
              })}
            </>
          )}

          {/* Empty state while typing */}
          {isTyping && placeRows.length === 0 && (
            <p className="px-4 py-3 text-sm text-gray-400">
              {isFetching ? 'Searching…' : 'No matching destinations — press Search to try anyway.'}
            </p>
          )}
        </div>
      )}
    </div>
  )
}

// ── Row ─────────────────────────────────────────────────────────────────────

function Row({
  icon, title, sub, badge, active, onMouseEnter, onClick,
}: {
  icon: 'recent' | 'pin'
  title: string
  sub?: string
  badge?: string
  active: boolean
  onMouseEnter: () => void
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onMouseEnter={onMouseEnter}
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-3 px-4 py-2 text-left transition-colors',
        active ? 'bg-gray-100' : 'hover:bg-gray-50',
      )}
    >
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-500">
        {icon === 'recent'
          ? <Clock className="h-4 w-4" aria-hidden />
          : <MapPin className="h-4 w-4" aria-hidden />}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium text-gray-900">{title}</span>
        {sub && <span className="block truncate text-xs text-gray-500">{sub}</span>}
      </span>
      {badge && (
        <span className="shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
          {badge}
        </span>
      )}
    </button>
  )
}
